import { Component, effect, ElementRef, forwardRef, input, ViewChild, output, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { defaultSettings } from '../settings/default.settings';
import { InputFile, isPublicFile, normalizePublicSequence } from '../interfaces/input-file';
import { InputFileRejected } from '../interfaces/input-file-rejected';
import { InputFileRejectedReason } from '../enums/input-file-rejected-reason';
import { InputFileService } from '../services/input-file.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { take } from 'rxjs';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ImageEditorDialogComponent } from '../image-editor/image-editor-dialog.component';
import { ImageEditorData, ImageEditorResult } from '../image-editor/image-editor.model';
import { ConfirmationDialogComponent } from '../../dialog/confirmation-dialog.component';

const EDITABLE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp'];
const MAX_IMAGE_DIMENSION = 1400;

@Component({ standalone: false,
    selector: 'input-file',
    templateUrl: './input-file.component.html',
    styleUrls: ['./input-file.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => InputFileComponent),
            multi: true
        }
    ]
})
export class InputFileComponent implements ControlValueAccessor, OnDestroy {
    @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

    public processing:boolean=false;
    public isDisabled: boolean = false;

    static nextId = 0;

    readonly disabled = input<boolean>(false);
    readonly editMode = input<boolean>(false);
    readonly fileAccept = input<string>(this.inputFileService.config.fileAccept || defaultSettings.fileAccept);
    readonly fileLimit = input<number>(this.inputFileService.config.fileLimit || defaultSettings.fileLimit);
    readonly iconDelete = input<string>(this.inputFileService.config.iconDelete || defaultSettings.iconDelete);
    readonly iconEdit = input<string>(this.inputFileService.config.iconEdit || defaultSettings.iconEdit);
    readonly iconFile = input<string>(this.inputFileService.config.iconFile || defaultSettings.iconFile);
    readonly sizeLimit = input<number>(this.inputFileService.config.sizeLimit || defaultSettings.sizeLimit);
    //image pool: each file can be selected for the public listing (publicSequenceNo)
    readonly publicSelection = input<boolean>(false);

    readonly rejectedFile = output<InputFileRejected>();
    readonly filesChanged = output<any>();

    public files = new Array<InputFile>();
    public id = `ngx-input-file-${InputFileComponent.nextId++}`;
    public onChange = (files: Array<InputFile>) => { };
    public onTouched = () => { };

    private isDestroyed: boolean = false;
    //object urls for image previews, created once per file and revoked when the file is gone
    private previewUrls = new Map<File, string>();

    get canAddFile(): boolean {
        return this.editMode() && this.files && this.files.length < this.fileLimit();
    }

    constructor(private dialog: MatDialog, private inputFileService: InputFileService, private snackBar: MatSnackBar) {
        effect(() => {
            this.isDisabled = this.disabled();
        });
    }

    //used by callers which populate the component directly (instead of via the form control)
    public addFile(blob:Blob, id:any, link:string){
        const inputFile: InputFile = { file: new File([blob], link, { type: blob.type }), id: id, link: link };
        const files = this.files.slice();
        const rejected = new Array<InputFileRejected>();
        if (this.fileGuard(files, inputFile, rejected)) files.push(inputFile);
        this.showRejected(rejected);
        this.updateFiles(files, false);
    }

    public clearFiles(){
        this.files = new Array<InputFile>();
        this.revokeUnusedPreviews();
    }

    ngOnDestroy(){
        this.isDestroyed = true;
        this.previewUrls.forEach(url => URL.revokeObjectURL(url));
        this.previewUrls.clear();
    }

    public asFileList(event: Event): FileList | null {
        return (event.target as HTMLInputElement).files;
    }

    public onDropped(event: CdkDragDrop<InputFile[]>) {
        if (event.previousIndex === event.currentIndex) return;
        const files = this.files.slice();
        moveItemInArray(files, event.previousIndex, event.currentIndex);
        this.updateFiles(files);
    }

    public onDeleteFile(index: number): void {
        if (this.isDisabled) return;
        const fileToDelete = this.files[index];
        if (!this.publicSelection() || !isPublicFile(fileToDelete)) {
            this.deleteFile(fileToDelete);
            return;
        }
        const dialogConfig = new MatDialogConfig();
        dialogConfig.data = { title: 'Delete photo', message: 'This photo is part of the public listing. Deleting it also removes it from the public listing. After saving, it is in the trash and can be restored from there for a while.', buttonText: { ok: 'Delete', cancel: 'Cancel' } };
        this.dialog.open(ConfirmationDialogComponent, dialogConfig).afterClosed().pipe(take(1)).subscribe(confirmed => {
            if (confirmed) this.deleteFile(fileToDelete);
        });
    }

    private deleteFile(fileToDelete: InputFile): void {
        this.updateFiles(normalizePublicSequence(this.files.filter(f => f !== fileToDelete)));
    }

    public isPublic(file: InputFile): boolean {
        return isPublicFile(file);
    }

    //private => last position of the public listing, public => private
    public onTogglePublic(index: number): void {
        if (this.isDisabled) return;
        const files = this.files.slice();
        const file = files[index];
        const publicCount = files.filter(isPublicFile).length;
        files[index] = { ...file, publicSequenceNo: isPublicFile(file) ? null : publicCount };
        this.updateFiles(normalizePublicSequence(files));
    }

    public onEditFile(index: number): void {
        if (this.isDisabled) return;
        this.openEditor(this.files[index]);
    }

    public canEditFile(index: number):boolean {
        const name = this.files[index]?.file?.name?.toLowerCase();
        return this.editMode() && !!name && EDITABLE_EXTENSIONS.some(ext => name.endsWith(ext));
    }

    public canDeleteFile(index: number):boolean {
        return this.editMode();
    }

    public canReplaceFile(index: number):boolean {
        return this.editMode();
    }

    private openEditor(fileToEdit:InputFile){
        const dialogConfig = new MatDialogConfig<ImageEditorData>();
        dialogConfig.disableClose = true;
        dialogConfig.autoFocus = false;
        dialogConfig.width = 'min(1100px, 100vw)';
        dialogConfig.height = 'min(820px, 100dvh)';
        dialogConfig.maxWidth = '100vw';
        dialogConfig.maxHeight = '100dvh';
        dialogConfig.data = { file: fileToEdit.file!, frame: fileToEdit.frame, fit: fileToEdit.fit, listingFrameEnabled: this.publicSelection() };

        this.dialog.open<ImageEditorDialogComponent, ImageEditorData, ImageEditorResult>(ImageEditorDialogComponent, dialogConfig).afterClosed().pipe(take(1)).subscribe(result=>{
            const index = this.files.indexOf(fileToEdit);
            if(!result || index === -1) return;
            const files = this.files.slice();
            const presentation = { frame: result.frame, fit: result.fit };
            if (!result.file) {
                //only listing frame/fit changed: same image (keeps its id, no new upload)
                files[index] = { ...fileToEdit, ...presentation };
            }
            else {
                //no id/link: an edited image is stored as a new image (on replace the original one gets removed).
                //A replacement takes over the public position, a copy starts private.
                const edited: InputFile = { file: result.file, ...presentation };
                if(result.asCopy){
                    const rejected = new Array<InputFileRejected>();
                    if (this.fileGuard(files, edited, rejected)) files.splice(index + 1, 0, edited);
                    this.showRejected(rejected);
                }
                else files[index] = { ...edited, publicSequenceNo: fileToEdit.publicSequenceNo };
            }
            this.updateFiles(files);
        });
    }

    public onReplaceFile(fileList: FileList | null, index: number, fileInput?: HTMLInputElement): void {
        if (!fileList?.length) return;
        this.replaceFile(fileList.item(0)!, index, fileInput);
    }

    public onReplaceFileDropped(files: File[], index: number): void {
        if (!files?.length) return;
        this.replaceFile(files[0], index);
    }

    private replaceFile(file: File, index: number, fileInput?: HTMLInputElement): void {
        if (this.isDisabled) return;
        this.processing = true;
        this.resizeImageFile(file, MAX_IMAGE_DIMENSION, MAX_IMAGE_DIMENSION).then(resizedFile => {
            const files = this.files.slice();
            //a replacement is a new image which takes over the public position
            const inputFile: InputFile = { file: resizedFile, publicSequenceNo: files[index]?.publicSequenceNo };
            const rejected = new Array<InputFileRejected>();
            //replacing does not change the number of files => bypass the limit check
            if (this.fileGuard(files, inputFile, rejected, true)) files[index] = inputFile;
            this.showRejected(rejected);
            this.updateFiles(files);
        }).catch(err => {
            console.error('Error processing file', err);
            this.processing = false;
        }).finally(() => {
            if (fileInput) fileInput.value = '';
        });
    }

    public onSelectFile(fileList: FileList | null): void {
        if (!fileList?.length) return;
        this.processSelectedFiles(Array.from(fileList));
    }

    public onFileDropped(files: File[]): void {
        if (!files?.length) return;
        this.processSelectedFiles(files);
    }

    private processSelectedFiles(files: File[]): void {
        if (this.isDisabled) return;
        this.processing = true;
        Promise.all(files.map(file => this.resizeImageFile(file, MAX_IMAGE_DIMENSION, MAX_IMAGE_DIMENSION))).then(resizedFiles => {
            const currentFiles = this.files.slice();
            const rejected = new Array<InputFileRejected>();
            resizedFiles.forEach(resizedFile => {
                const inputFile: InputFile = { file: resizedFile };
                if (this.fileGuard(currentFiles, inputFile, rejected)) currentFiles.push(inputFile);
            });
            this.showRejected(rejected);
            this.updateFiles(currentFiles);
        }).catch(err => {
            console.error('Error processing files', err);
            this.processing = false;
        }).finally(() => {
            if (this.fileInput) this.fileInput.nativeElement.value = '';
        });
    }

    //Implementation of ControlValueAccessor.
    public registerOnChange(fn: (files: Array<InputFile>) => void): void {
        this.onChange = fn;
    }

    //Implementation of ControlValueAccessor.
    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    //Implementation of ControlValueAccessor.
    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    //Implementation of ControlValueAccessor. Model => view only, must not report a change back.
    public writeValue(files: Array<InputFile>): void {
        this.files = files ?? new Array<InputFile>();
        this.setFilePreview();
        this.processing = false;
    }

    //view => model: every change made in the component goes through here
    private updateFiles(files: Array<InputFile>, emitFilesChanged: boolean = true): void {
        this.writeValue(files);
        this.onChange(this.files);
        this.onTouched();
        if (emitFilesChanged && !this.isDestroyed) this.filesChanged.emit(undefined);
    }

    private resizeImageFile(file:File, maxWidth:number, maxHeight:number):Promise<File> {
        //only resize image files
        if (!this.inputFileService.typeGuard(file, 'image/*')) return Promise.resolve(file);
        return new Promise((resolve, reject) => {
            const url = URL.createObjectURL(file);
            const image = new Image();
            image.onload = () => {
                URL.revokeObjectURL(url);
                const width = image.width;
                const height = image.height;
                if (width <= maxWidth && height <= maxHeight) {
                    resolve(file);
                    return;
                }
                const ratio = Math.min(maxWidth / width, maxHeight / height);
                const canvas = document.createElement('canvas');
                canvas.width = Math.round(width * ratio);
                canvas.height = Math.round(height * ratio);
                canvas.getContext('2d')!.drawImage(image, 0, 0, canvas.width, canvas.height);
                canvas.toBlob(blob => {
                    if (blob) resolve(this.toFile(blob, file.name));
                    else reject(new Error('Resizing ' + file.name + ' failed'));
                }, file.type);
            };
            image.onerror = (err) => {
                URL.revokeObjectURL(url);
                reject(err);
            };
            image.src = url;
        });
    }

    private toFile(blob: Blob, fileName: string): File {
        return new File([blob], fileName, { type: blob.type });
    }

    // Whether the file can be added to the model. Rejections are collected in rejected.
    private fileGuard(files: Array<InputFile>, file: InputFile, rejected: Array<InputFileRejected>, bypassLimit?: boolean): boolean {
        let reason: InputFileRejectedReason | undefined;
        if (!bypassLimit && !this.inputFileService.limitGuard(files, this.fileLimit())) reason = InputFileRejectedReason.limitReached;
        else if (!this.inputFileService.sizeGuard(file.file!, this.sizeLimit())) reason = InputFileRejectedReason.sizeReached;
        else if (!this.inputFileService.typeGuard(file.file!, this.fileAccept())) reason = InputFileRejectedReason.badFile;
        if (reason === undefined) return true;

        const r: InputFileRejected = { reason, file };
        rejected.push(r);
        if (!this.isDestroyed) this.rejectedFile.emit(r);
        return false;
    }

    private showRejected(rejected: Array<InputFileRejected>): void {
        if (rejected.length === 0) return;
        const count = (reason: InputFileRejectedReason) => rejected.filter(r => r.reason === reason).length;
        const messages = new Array<string>();
        if (count(InputFileRejectedReason.limitReached)) messages.push(`${count(InputFileRejectedReason.limitReached)} skipped (limit of ${this.fileLimit()} reached)`);
        if (count(InputFileRejectedReason.sizeReached)) messages.push(`${count(InputFileRejectedReason.sizeReached)} too large (max ${this.sizeLimit()} MB)`);
        if (count(InputFileRejectedReason.badFile)) messages.push(`${count(InputFileRejectedReason.badFile)} not supported (${this.fileAccept()})`);
        this.snackBar.open('Files not added: ' + messages.join(', '), '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top' });
    }

    //Sets the image previews from cached object urls
    private setFilePreview(): void {
        for (const f of this.files) {
            if (f.file && this.inputFileService.typeGuard(f.file, 'image/*')) {
                let url = this.previewUrls.get(f.file);
                if (!url) {
                    url = URL.createObjectURL(f.file);
                    this.previewUrls.set(f.file, url);
                }
                f.preview = url;
            }
        }
        this.revokeUnusedPreviews();
    }

    private revokeUnusedPreviews(): void {
        const inUse = new Set(this.files.map(f => f.file));
        this.previewUrls.forEach((url, file) => {
            if (!inUse.has(file)) {
                URL.revokeObjectURL(url);
                this.previewUrls.delete(file);
            }
        });
    }
}
